// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Paper,
  Stack,
  Tabs,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Switch,
  SegmentedControl,
  Avatar,
  Text,
  Divider,
  Badge,
  useMantineColorScheme,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { IconUser, IconLock, IconBell, IconPalette } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '@ferp/auth';
import { useChangePassword } from '../../hooks/useSettings';

export function MyAccountPage() {
  const { user } = useAuth();
  const profile = user as unknown as { firstName?: string; lastName?: string; email?: string; role?: string; companyName?: string } | null;
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const [notificationPrefs, setNotificationPrefs] = useState({
    payrollCompleted: true,
    timeOffRequests: true,
    timesheetExceptions: true,
    systemAlerts: true,
    weeklySummary: false,
  });

  const passwordForm = useForm({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (v: string) => (v.length < 1 ? 'Required' : null),
      newPassword: (v: string) => (v.length < 8 ? 'Must be at least 8 characters' : null),
      confirmPassword: (v: string, values: { newPassword: string }) =>
        v !== values.newPassword ? 'Passwords do not match' : null,
    },
  });

  const changePasswordHook = useChangePassword();
  const passwordLoading = changePasswordHook.isPending;

  const handlePasswordChange = passwordForm.onSubmit((values) => {
    changePasswordHook.mutate(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Password Changed',
            message: 'Your password has been updated successfully.',
            color: 'green',
          });
          passwordForm.reset();
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to change password. Please check your current password.',
            color: 'red',
          });
        },
      },
    );
  });

  const formatRole = (role: string) =>
    role
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Stack gap="md">
      <PageHeader
        title="My Account"
        subtitle="Manage your profile and preferences"
      />

      <Paper withBorder p="lg" radius="md">
        <Tabs defaultValue="profile">
          <Tabs.List>
            <Tabs.Tab value="profile" icon={<IconUser size={16} />}>
              Profile
            </Tabs.Tab>
            <Tabs.Tab value="security" icon={<IconLock size={16} />}>
              Security
            </Tabs.Tab>
            <Tabs.Tab value="notifications" icon={<IconBell size={16} />}>
              Notifications
            </Tabs.Tab>
            <Tabs.Tab value="appearance" icon={<IconPalette size={16} />}>
              Appearance
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="lg">
            <Stack gap="md" maw={500}>
              <Group>
                <Avatar size={80} radius="xl" color="blue">
                  {profile?.firstName?.charAt(0)}
                  {profile?.lastName?.charAt(0)}
                </Avatar>
                <div>
                  <Text fw={600} size="lg">
                    {profile?.firstName} {profile?.lastName}
                  </Text>
                  <Badge variant="light" size="sm" mt={4}>
                    {profile?.role ? formatRole(profile.role) : 'Unknown'}
                  </Badge>
                </div>
              </Group>

              <Divider />

              <TextInput
                label="First Name"
                value={profile?.firstName ?? ''}
                readOnly
              />
              <TextInput
                label="Last Name"
                value={profile?.lastName ?? ''}
                readOnly
              />
              <TextInput
                label="Email"
                value={profile?.email ?? ''}
                readOnly
              />
              <TextInput
                label="Role"
                value={profile?.role ? formatRole(profile.role) : ''}
                readOnly
              />
              <TextInput
                label="Company"
                value={profile?.companyName ?? ''}
                readOnly
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="security" pt="lg">
            <form onSubmit={handlePasswordChange}>
              <Stack gap="md" maw={400}>
                <Text fw={600}>Change Password</Text>
                <PasswordInput
                  label="Current Password"
                  required
                  {...passwordForm.getInputProps('currentPassword')}
                />
                <PasswordInput
                  label="New Password"
                  required
                  description="Must be at least 8 characters"
                  {...passwordForm.getInputProps('newPassword')}
                />
                <PasswordInput
                  label="Confirm New Password"
                  required
                  {...passwordForm.getInputProps('confirmPassword')}
                />
                <Group justify="flex-end">
                  <Button type="submit" loading={passwordLoading}>
                    Change Password
                  </Button>
                </Group>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="notifications" pt="lg">
            <Stack gap="md" maw={500}>
              <Text fw={600}>Email Notifications</Text>
              <Switch
                label="Payroll run completed"
                description="Receive an email when a payroll run completes"
                checked={notificationPrefs.payrollCompleted}
                onChange={(e) =>
                  setNotificationPrefs((prev) => ({
                    ...prev,
                    payrollCompleted: e.currentTarget.checked,
                  }))
                }
              />
              <Switch
                label="Time-off requests"
                description="Receive an email when employees submit time-off requests"
                checked={notificationPrefs.timeOffRequests}
                onChange={(e) =>
                  setNotificationPrefs((prev) => ({
                    ...prev,
                    timeOffRequests: e.currentTarget.checked,
                  }))
                }
              />
              <Switch
                label="Timesheet exceptions"
                description="Receive an email for critical timesheet exceptions"
                checked={notificationPrefs.timesheetExceptions}
                onChange={(e) =>
                  setNotificationPrefs((prev) => ({
                    ...prev,
                    timesheetExceptions: e.currentTarget.checked,
                  }))
                }
              />
              <Switch
                label="System alerts"
                description="Important system notifications and updates"
                checked={notificationPrefs.systemAlerts}
                onChange={(e) =>
                  setNotificationPrefs((prev) => ({
                    ...prev,
                    systemAlerts: e.currentTarget.checked,
                  }))
                }
              />
              <Switch
                label="Weekly summary"
                description="Weekly workforce summary report"
                checked={notificationPrefs.weeklySummary}
                onChange={(e) =>
                  setNotificationPrefs((prev) => ({
                    ...prev,
                    weeklySummary: e.currentTarget.checked,
                  }))
                }
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="appearance" pt="lg">
            <Stack gap="md" maw={400}>
              <div>
                <Text fw={600} mb="xs">
                  Color Scheme
                </Text>
                <SegmentedControl
                  value={colorScheme}
                  onChange={(val) => setColorScheme(val as 'light' | 'dark' | 'auto')}
                  data={[
                    { value: 'light', label: 'Light' },
                    { value: 'dark', label: 'Dark' },
                    { value: 'auto', label: 'Auto' },
                  ]}
                  fullWidth
                />
                <Text size="xs" c="dimmed" mt={4}>
                  Auto follows your system preference
                </Text>
              </div>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  );
}

export default MyAccountPage;
